import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card'
import { Button } from '@shared/components/ui/button'
import { Input } from '@shared/components/ui/input'
import { Label } from '@shared/components/ui/label'
import { Badge } from '@shared/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@shared/components/ui/dialog'
import { Checkbox } from '@shared/components/ui/checkbox'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/table'
import { toast } from '@shared/hooks/use-toast'
import { apiUrl } from '@/lib/api'
import { PageHeader } from '@shared/components/ui/page-header'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faEdit, faTrash, faShieldHalved, faSearch } from '@fortawesome/free-solid-svg-icons'

interface Role {
  id: string
  name: string
  description?: string
  is_system_role: boolean
  created_at: string
  updated_at: string
  permissions: Array<{
    id: string
    key: string
    name: string
    description?: string
  }>
}

interface Permission {
  id: string
  key: string
  name: string
  description?: string
  category?: string
}

const AdminRolesPage: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([])
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [selectedRole, setSelectedRole] = useState<Role | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissionKeys: [] as string[]
  })

  useEffect(() => {
    loadRoles()
    loadPermissions()
  }, [])

  const loadRoles = async () => {
    setLoading(true)
    try {
      const { auth } = await import('@/lib/firebase')
      const token = await auth.currentUser?.getIdToken()
      if (!token) {
        console.error('No authentication token available')
        return
      }

      const response = await fetch(apiUrl('/api/admin/roles'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setRoles(data.roles || [])
      } else {
        toast({
          title: 'Error',
          description: 'Failed to load admin roles',
          variant: 'destructive'
        })
      }
    } catch (error) {
      console.error('Failed to load roles:', error)
      toast({
        title: 'Error',
        description: 'Failed to load admin roles',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  const loadPermissions = async () => {
    try {
      const { auth } = await import('@/lib/firebase')
      const token = await auth.currentUser?.getIdToken()
      if (!token) return

      const response = await fetch(apiUrl('/api/admin/permissions'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setPermissions(data.permissions || [])
      }
    } catch (error) {
      console.error('Failed to load permissions:', error)
    }
  }

  const handleCreateRole = async () => {
    if (!formData.name) {
      toast({
        title: 'Error',
        description: 'Please enter a role name',
        variant: 'destructive'
      })
      return
    }

    try {
      const { auth } = await import('@/lib/firebase')
      const token = await auth.currentUser?.getIdToken()
      if (!token) return

      const response = await fetch(apiUrl('/api/admin/roles'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Admin role created successfully'
        })
        setIsCreateDialogOpen(false)
        setFormData({ name: '', description: '', permissionKeys: [] })
        loadRoles()
      } else {
        const error = await response.json().catch(() => ({}))
        toast({
          title: 'Error',
          description: error.error || 'Failed to create admin role',
          variant: 'destructive'
        })
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create admin role',
        variant: 'destructive'
      })
    }
  }

  const handleEditRole = async () => {
    if (!selectedRole || !formData.name) {
      toast({
        title: 'Error',
        description: 'Please enter a role name',
        variant: 'destructive'
      })
      return
    }

    try {
      const { auth } = await import('@/lib/firebase')
      const token = await auth.currentUser?.getIdToken()
      if (!token) return

      const response = await fetch(apiUrl(`/api/admin/roles/${selectedRole.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Admin role updated successfully'
        })
        setIsEditDialogOpen(false)
        setSelectedRole(null)
        setFormData({ name: '', description: '', permissionKeys: [] })
        loadRoles()
      } else {
        const error = await response.json().catch(() => ({}))
        toast({
          title: 'Error',
          description: error.error || 'Failed to update admin role',
          variant: 'destructive'
        })
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update admin role',
        variant: 'destructive'
      })
    }
  }

  const handleDeleteRole = async (roleId: string) => {
    if (!confirm('Are you sure you want to delete this admin role?')) return

    try {
      const { auth } = await import('@/lib/firebase')
      const token = await auth.currentUser?.getIdToken()
      if (!token) return

      const response = await fetch(apiUrl(`/api/admin/roles/${roleId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Admin role deleted successfully'
        })
        loadRoles()
      } else {
        const error = await response.json().catch(() => ({}))
        toast({
          title: 'Error',
          description: error.error || 'Failed to delete admin role',
          variant: 'destructive'
        })
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete admin role',
        variant: 'destructive'
      })
    }
  }

  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (role.description && role.description.toLowerCase().includes(searchQuery.toLowerCase()))
  )

  const groupedPermissions = permissions.reduce((acc, permission) => {
    const category = permission.category || 'General'
    if (!acc[category]) {
      acc[category] = []
    }
    acc[category].push(permission)
    return acc
  }, {} as Record<string, Permission[]>)

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Admin Roles & Permissions" 
        description="Manage admin roles and their permissions"
      />

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Admin Roles</CardTitle>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <FontAwesomeIcon icon={faPlus} className="mr-2 h-4 w-4" />
                  Add Role
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Create Admin Role</DialogTitle>
                  <DialogDescription>
                    Create a new admin role with specific permissions.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">Role Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Support Manager"
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Input
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Brief description of the role"
                    />
                  </div>
                  <div>
                    <Label>Permissions</Label>
                    <div className="max-h-60 overflow-y-auto border rounded-md p-4 space-y-4">
                      {Object.entries(groupedPermissions).map(([category, categoryPermissions]) => (
                        <div key={`create-${category}`}>
                          <h4 className="font-medium text-sm text-muted-foreground mb-2">{category}</h4>
                          <div className="space-y-2">
                            {categoryPermissions.map((permission, index) => (
                              <div key={`create-${permission.id}-${index}`} className="flex items-center space-x-2">
                                <Checkbox
                                  id={permission.id}
                                  checked={formData.permissionKeys.includes(permission.key)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setFormData(prev => ({
                                        ...prev,
                                        permissionKeys: [...prev.permissionKeys, permission.key]
                                      }))
                                    } else {
                                      setFormData(prev => ({
                                        ...prev,
                                        permissionKeys: prev.permissionKeys.filter(key => key !== permission.key)
                                      }))
                                    }
                                  }}
                                />
                                <Label htmlFor={permission.id} className="text-sm">
                                  {permission.name}
                                  {permission.description && (
                                    <span className="text-muted-foreground ml-1">- {permission.description}</span>
                                  )}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateRole}>
                    Create Role
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="relative">
              <FontAwesomeIcon 
                icon={faSearch} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" 
              />
              <Input
                placeholder="Search roles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role</TableHead>
                <TableHead>Permissions</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredRoles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    No admin roles found
                  </TableCell>
                </TableRow>
              ) : (
                filteredRoles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{role.name}</div>
                        {role.description && (
                          <div className="text-sm text-muted-foreground">{role.description}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {role.permissions.slice(0, 3).map(permission => (
                          <Badge key={permission.id} variant="secondary" className="text-xs">
                            {permission.name}
                          </Badge>
                        ))}
                        {role.permissions.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{role.permissions.length - 3} more
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={role.is_system_role ? 'default' : 'secondary'}>
                        {role.is_system_role ? 'System' : 'Custom'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(role.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedRole(role)
                            setFormData({
                              name: role.name,
                              description: role.description || '',
                              permissionKeys: role.permissions.map(p => p.key)
                            })
                            setIsEditDialogOpen(true)
                          }}
                        >
                          <FontAwesomeIcon icon={faEdit} className="h-4 w-4" />
                        </Button>
                        {!role.is_system_role && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteRole(role.id)}
                          >
                            <FontAwesomeIcon icon={faTrash} className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Admin Role</DialogTitle>
            <DialogDescription>
              Update role information and permissions.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Role Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="edit-description">Description</Label>
              <Input
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div>
              <Label>Permissions</Label>
              <div className="max-h-60 overflow-y-auto border rounded-md p-4 space-y-4">
                {Object.entries(groupedPermissions).map(([category, categoryPermissions]) => (
                  <div key={`edit-${category}`}>
                    <h4 className="font-medium text-sm text-muted-foreground mb-2">{category}</h4>
                    <div className="space-y-2">
                      {categoryPermissions.map((permission, index) => (
                        <div key={`edit-${permission.id}-${index}`} className="flex items-center space-x-2">
                          <Checkbox
                            id={`edit-${permission.id}`}
                            checked={formData.permissionKeys.includes(permission.key)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setFormData(prev => ({
                                  ...prev,
                                  permissionKeys: [...prev.permissionKeys, permission.key]
                                }))
                              } else {
                                setFormData(prev => ({
                                  ...prev,
                                  permissionKeys: prev.permissionKeys.filter(key => key !== permission.key)
                                }))
                              }
                            }}
                          />
                          <Label htmlFor={`edit-${permission.id}`} className="text-sm">
                            {permission.name}
                            {permission.description && (
                              <span className="text-muted-foreground ml-1">- {permission.description}</span>
                            )}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditRole}>
              Update Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default AdminRolesPage
